import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {removeTerminalVideo} from '@/entities/stream/api';
import {fetchControlDepartments} from '@/entities/attendance/api';
import {
  finalizeDeviceReset,
  forceSync,
  moveDeviceToDepartment,
  renameDevice,
  type SyncMode,
} from '@/entities/device/api';
import type {DeviceSummary} from '@/entities/device/types';
import {ApiError} from '@/shared/api';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {AppInput} from '@/shared/ui/input';
import {SectionHeader} from '@/shared/ui/section';
import {ChoiceChips, Segmented} from '@/shared/ui/segmented';
import {AppText} from '@/shared/ui/text';
import {spacing} from '@/shared/ui/theme';

/**
 * The device operations the cabinet could not do before: rename it, move it between
 * departments, push a re-sync, and remove it.
 *
 * ## The department move, and why it re-reads
 *
 * `PATCH /control/devices/{id}/department` has a trap the gateway's own defect register
 * records: an id sv_control cannot resolve is stored as **NULL and answered `Ok`**, not
 * as an error (device_repo.rs: "fall back to NULL rather than storing the unresolvable
 * raw id"). The usual way to hit it is to create a department and assign it seconds
 * later, before the organization→control projection has caught up — the call succeeds,
 * the field is empty, and nothing anywhere says so.
 *
 * `moveDeviceToDepartment` (entities/device/api) closes it by writing, reading back and
 * retrying once. This panel therefore reports a failed move as a failure instead of
 * showing a department that was never saved.
 *
 * ## Deleting, and the route it does NOT use
 *
 * The obvious route is `DELETE /control/devices/{id}`, and it is the wrong one: it
 * clears the device's rows in sv_control and leaves its LICENCE SEAT occupied. An owner
 * who removes and re-adopts four terminals that way silently burns four seats, with no
 * screen anywhere to explain where they went.
 *
 * `POST /control/devices/{id}/factory-reset/finalize` does the same removal and
 * releases the seats. It needs nothing from the terminal — so it also works on a board
 * that is lost, stolen or bricked — and it is idempotent, so a half-finished removal is
 * healed by pressing the button again. It is the route the API guide names for this
 * button, and the reason `deleteDevice` is not imported here.
 *
 * What it does not do is wipe the board. A terminal still standing on a wall keeps its
 * faces and its settings until the factory reset below this panel is run; the copy says
 * so, rather than letting "удалить" imply "decommissioned".
 */
const styles = StyleSheet.create({
  root: {gap: spacing(4)},
  block: {gap: spacing(2)},
  card: {gap: spacing(3)},
  row: {flexDirection: 'row', gap: spacing(2), flexWrap: 'wrap'},
  grow: {flexGrow: 1, flexBasis: 150},
});

export function DeviceAdminPanel({
  companyId,
  device,
  onDeleted,
}: {
  companyId: string;
  device: DeviceSummary;
  onDeleted: () => void;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();

  const [name, setName] = useState(device.device_name);
  const [syncMode, setSyncMode] = useState<SyncMode>('full');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const departments = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () => fetchControlDepartments(companyId),
  });

  const invalidate = async () => {
    await qc.invalidateQueries({queryKey: ['device', companyId, device.device_id]});
    await qc.invalidateQueries({queryKey: ['devices', companyId]});
  };

  const rename = useMutation({
    mutationFn: () => renameDevice(companyId, device.device_id, name.trim()),
    onSuccess: invalidate,
  });

  const move = useMutation({
    mutationFn: (departmentId: string) =>
      moveDeviceToDepartment(companyId, device.device_id, departmentId),
    onSuccess: invalidate,
  });

  const sync = useMutation({
    mutationFn: () => forceSync(companyId, device.device_id, syncMode),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    // finalize, never DELETE — see the note at the top of this file.
    mutationFn: async () => {
      await finalizeDeviceReset(companyId, device.device_id);
      // Его видео — отдельный источник; без этого камера терминала переживала его.
      await removeTerminalVideo(companyId, device);
    },
    onSuccess: async () => {
      await invalidate();
      await qc.invalidateQueries({queryKey: ['stream-cameras', companyId]});
      onDeleted();
    },
  });

  const errorOf = (e: unknown): string =>
    e instanceof ApiError ? e.message : t('errors.generic');

  const departmentOptions = [
    {value: '', label: t('deviceAdmin.noDepartment')},
    ...(departments.data?.departments ?? []).map(d => ({
      value: d.department_id,
      label: d.name,
    })),
  ];

  return (
    <View style={styles.root}>
      <View style={styles.block}>
        <SectionHeader title={t('deviceAdmin.identity')} />
        <Card padding={4} style={styles.card}>
          <AppInput
            label={t('deviceAdmin.name')}
            value={name}
            onChangeText={setName}
            testID="device-name"
          />
          {rename.isError ? (
            <Banner tone="error" title={errorOf(rename.error)} />
          ) : null}
          {rename.isSuccess ? (
            <Banner tone="success" title={t('account.saved')} />
          ) : null}
          <AppButton
            title={t('actions.save')}
            size="sm"
            disabled={name.trim().length === 0 || name.trim() === device.device_name}
            loading={rename.isPending}
            onPress={() => rename.mutate()}
            testID="device-rename"
          />
        </Card>
      </View>

      <View style={styles.block}>
        <SectionHeader
          title={t('deviceAdmin.department')}
          hint={t('deviceAdmin.departmentHint')}
        />
        <Card padding={4} style={styles.card}>
          <ChoiceChips
            value={device.department_id ?? ''}
            onChange={id => move.mutate(id)}
            disabled={move.isPending}
            options={departmentOptions}
            testID="device-department"
          />
          {move.isError ? (
            <Banner
              tone="error"
              title={
                // The one failure worth naming precisely: the write "succeeded" and
                // the field did not take. See the module note.
                (move.error as Error)?.message === 'department_not_applied'
                  ? t('deviceAdmin.departmentNotApplied')
                  : errorOf(move.error)
              }
              message={
                (move.error as Error)?.message === 'department_not_applied'
                  ? t('deviceAdmin.departmentNotAppliedHint')
                  : undefined
              }
            />
          ) : null}
        </Card>
      </View>

      <View style={styles.block}>
        <SectionHeader
          title={t('deviceAdmin.sync')}
          hint={t('deviceAdmin.syncHint')}
        />
        <Card padding={4} style={styles.card}>
          <Segmented
            value={syncMode}
            onChange={setSyncMode}
            testID="device-sync-mode"
            options={[
              {value: 'full', label: t('deviceAdmin.syncFull')},
              {value: 'settings', label: t('deviceAdmin.syncSettings')},
              {value: 'personnel', label: t('deviceAdmin.syncPersonnel')},
            ]}
          />
          {sync.isError ? <Banner tone="error" title={errorOf(sync.error)} /> : null}
          {sync.isSuccess ? (
            <Banner tone="success" title={t('deviceAdmin.syncQueued')} />
          ) : null}
          <AppButton
            title={t('deviceAdmin.syncNow')}
            tone="soft"
            size="sm"
            loading={sync.isPending}
            onPress={() => sync.mutate()}
            testID="device-force-sync"
          />
        </Card>
      </View>

      <View style={styles.block}>
        <SectionHeader title={t('deviceAdmin.remove')} />
        <Card padding={4} style={styles.card}>
          <AppText variant="caption">{t('deviceAdmin.removeHint')}</AppText>
          {confirmDelete ? (
            <>
              <Banner
                tone="warning"
                title={t('deviceAdmin.removeConfirm')}
                message={t('deviceAdmin.removeSeats')}
              />
              {remove.isError ? (
                <Banner tone="error" title={errorOf(remove.error)} />
              ) : null}
              <View style={styles.row}>
                <AppButton
                  title={t('deviceAdmin.removeDo')}
                  tone="destructive"
                  size="sm"
                  loading={remove.isPending}
                  onPress={() => remove.mutate()}
                  style={styles.grow}
                  testID="device-delete-confirm"
                />
                <AppButton
                  title={t('actions.cancel')}
                  tone="ghost"
                  size="sm"
                  onPress={() => setConfirmDelete(false)}
                  style={styles.grow}
                />
              </View>
            </>
          ) : (
            <AppButton
              title={t('deviceAdmin.remove')}
              tone="neutral"
              size="sm"
              onPress={() => setConfirmDelete(true)}
              testID="device-delete"
            />
          )}
        </Card>
      </View>
    </View>
  );
}
